import { useEffect } from "react";

const SITE = {
  name: "No Fuss Websites",
  url: "https://nofusswebsites.com.au",
  phone: "0466 476 264",
  email: "hello@nofusswebsites.com.au",
  abn: "57 371 480 477",
  address: { city: "Melbourne", state: "VIC", country: "AU" },
  logo: "https://nofusswebsites.com.au/logo.png",
};

function setMeta(name, content, property = false) {
  const attr = property ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function SEOHead({ title, description, path, schema, noindex = false, canonical, image }) {
  const fullTitle = title ? `${title} | No Fuss Websites` : "No Fuss Websites — Professional Websites from $499 | Melbourne";
  const fullUrl = `${SITE.url}${path || ""}`;
  const canonicalUrl = canonical || fullUrl;
  const desc = description || "";
  const ogImage = image
    ? (image.startsWith("http") ? image : `${SITE.url}${image}`)
    : `${SITE.url}/og-image.jpg`;

  useEffect(() => {
    document.title = fullTitle;
    setMeta("description", desc);
    setLink("canonical", canonicalUrl);
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");
    setMeta("og:title", fullTitle, true);
    setMeta("og:description", desc, true);
    setMeta("og:url", fullUrl, true);
    setMeta("og:type", "website", true);
    setMeta("og:site_name", "No Fuss Websites", true);
    setMeta("og:locale", "en_AU", true);
    setMeta("og:image", ogImage, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", ogImage);
  }, [fullTitle, desc, canonicalUrl, fullUrl, noindex, ogImage]);

  // Inject JSON-LD schemas
  useEffect(() => {
    if (!schema) return;
    const schemas = Array.isArray(schema) ? schema.flat() : [schema];
    const scripts = schemas.map((s) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seo", "true");
      el.textContent = JSON.stringify(s);
      document.head.appendChild(el);
      return el;
    });
    return () => scripts.forEach((el) => el.remove());
  }, [schema]);

  return null;
}

// Schema generators
export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": SITE.name,
    "url": SITE.url,
    "logo": SITE.logo,
    "telephone": "+61466476264",
    "email": SITE.email,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Melbourne",
      "addressRegion": "VIC",
      "addressCountry": "AU"
    },
    "geo": { "@type": "GeoCoordinates", "latitude": -37.8136, "longitude": 144.9631 },
    "areaServed": { "@type": "Country", "name": "Australia" },
    "priceRange": "$$",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "17:00"
    },
    "sameAs": [],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "47",
      "bestRating": "5"
    }
  };
}

export function serviceSchema(name, description, price) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": name,
    "provider": { "@type": "ProfessionalService", "name": SITE.name, "url": SITE.url },
    "description": description,
    "areaServed": { "@type": "Country", "name": "Australia" },
    ...(price && {
      "offers": {
        "@type": "Offer",
        "price": price,
        "priceCurrency": "AUD",
        "availability": "https://schema.org/InStock"
      }
    })
  };
}

export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map(({ q, a }) => ({
      "@type": "Question",
      "name": q,
      "acceptedAnswer": { "@type": "Answer", "text": a }
    }))
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `${SITE.url}${item.path}`
    }))
  };
}

export function reviewSchema(reviews) {
  return reviews.map(r => ({
    "@context": "https://schema.org",
    "@type": "Review",
    "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": "5" },
    "author": { "@type": "Person", "name": r.client_name },
    "reviewBody": r.review,
    "itemReviewed": { "@type": "ProfessionalService", "name": SITE.name }
  }));
}

export function productSchema(packages) {
  return packages.map(pkg => ({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `${pkg.name} Website Package`,
    "description": pkg.tagline,
    "brand": { "@type": "Brand", "name": SITE.name },
    "offers": {
      "@type": "Offer",
      "price": pkg.price,
      "priceCurrency": "AUD",
      "availability": "https://schema.org/InStock",
      "url": `${SITE.url}/purchase?package=${pkg.id || pkg.name.toLowerCase()}`
    }
  }));
}

export function articleSchema(title, description, path, author, datePublished) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "url": `${SITE.url}${path}`,
    "author": { "@type": "Person", "name": author || "No Fuss Websites Team" },
    "publisher": { "@type": "Organization", "name": SITE.name, "url": SITE.url, "logo": { "@type": "ImageObject", "url": SITE.logo } },
    "datePublished": datePublished,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${SITE.url}${path}` }
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE.name,
    "url": SITE.url,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${SITE.url}/?s={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
}

export { SITE };
